/**
 * How the specialist home's state should RENDER — as a pure function, because
 * the distinction it protects is the one an operator acts on.
 *
 * ⚖ THE SPLIT (lexserver `6301d31b4`): `disabled` and `broken` both refuse every
 * consumer surface, and they are NOT interchangeable. `disabled` = no root
 * configured, the feature is off, a legitimate deployment. `broken` = a root IS
 * configured and could not be made good — a fault, carrying a reason worth
 * paging on. A UI that renders them the same either raises an incident over a
 * deployment choice or stays quiet through a real one.
 *
 * ⚠ AND A THIRD THING THAT IS NEITHER: a failed READ. `disabled` is also the
 * server's PRE-BOOT DEFAULT (`specialist-home.ts` sets it deliberately so a
 * surface reading before boot refuses rather than throws). So "the server says
 * disabled" and "we could not ask the server" arrive looking identical, on the
 * one page whose job is to tell an operator which is true. `unread` exists to
 * keep them apart — the same rule the Agent Bus tab had to learn by shipping
 * its inverse (`bus-section-state.ts`), and the one this section's roster
 * already applies per column (`specialist-column.ts`).
 *
 * ⚠ AND A FOURTH: a state string this client does not recognise. lexserver may
 * add one; the safe direction is to say so, never to fall through to healthy.
 */

import type { Probe } from "./probe";
import type { SpecialistHomeState } from "./specialists-api";

export type HomeView =
  /** We could not ask, or have not asked yet. NOT a health verdict. */
  | { kind: "unread"; reason: string }
  /** No root configured — the feature is off. A deployment, not an incident. */
  | { kind: "disabled" }
  /** Configured but not good. The only one that is an incident. */
  | { kind: "broken"; root: string; reason: string }
  | {
      kind: "ready";
      root: string;
      sharedPath: string;
      roster: string[];
      created: string[];
      commit: string | null;
    }
  /** A state this client has no rendering for. Representable, never healthy. */
  | { kind: "unrecognized"; state: string };

/** How loudly a view should read. `unread` is deliberately NOT `ok`. */
export type HomeSeverity = "ok" | "notice" | "incident" | "unread";

export function homeSeverity(view: HomeView): HomeSeverity {
  switch (view.kind) {
    case "ready":
      return "ok";
    case "disabled":
      return "notice";
    case "broken":
    case "unrecognized":
      return "incident";
    case "unread":
      return "unread";
  }
}

/**
 * Turn a settled probe of `GET /state` into the view to render.
 *
 * `null` means the probe has not been run yet — which is `unread`, not empty.
 */
export function selectHomeView(
  p: Probe<{ home: SpecialistHomeState }> | null | undefined,
): HomeView {
  if (!p) return { kind: "unread", reason: "not read yet" };
  if (!p.ok) return { kind: "unread", reason: p.error || "the read failed without a stated reason" };

  const home = p.value?.home as SpecialistHomeState | undefined;
  if (!home || typeof home.state !== "string") {
    // A 200 whose body is not the shape we asked for is a failed read wearing a
    // success status. It is not "disabled".
    return { kind: "unread", reason: "the server returned no home state" };
  }

  switch (home.state) {
    case "disabled":
      return { kind: "disabled" };
    case "broken":
      return {
        kind: "broken",
        root: home.root,
        // Never fabricate a reason; never render a blank one either.
        reason: home.reason?.trim() || "reported broken, without a stated reason",
      };
    case "ready":
      return {
        kind: "ready",
        root: home.root,
        sharedPath: home.sharedPath,
        roster: Array.isArray(home.roster) ? home.roster : [],
        created: Array.isArray(home.created) ? home.created : [],
        commit: home.commit ?? null,
      };
    default:
      return { kind: "unrecognized", state: String((home as { state: string }).state) };
  }
}

/**
 * The roster of specialists that own a workspace — i.e. what S4 can browse.
 *
 * `null` (not an empty array) when the home could not be read or is not ready:
 * an empty picker and an unreadable one are different, and only one of them
 * means "this platform has no specialist workspaces".
 */
export function browsableRoster(view: HomeView): string[] | null {
  return view.kind === "ready" ? view.roster : null;
}
